import type {
    CoverableChangesDTO,
    CoverableDTO,
    DiffDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import type { OPCRowInformation } from '../../../../config/flavourConfig';
import type {
    YouthContentDTO,
    PrivateContentDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/content';
import { getTermMonetaryValue } from '../../../../utils/termsUtil';
import { getCoverageFromCoverable } from '../../../../utils/coverablesUtil';
import ViewHippoInformationModal from '../../../../shared_components/ViewHippoInformationModal/ViewHippoInformationModal';
import { getCoverableDiffsDTO, isChangeTypeAvailable } from '../../../../utils/opcChangesUtil';
import { AdjustItemKeys } from '../adjustDetails';

const CONTENT_BAGGAGE_COVERAGE = 'PRCContentAndBaggageCov';
const CONTENT_BAGGAGE_DEDUCTIBLE = 'PRCContentAndBaggageCovDeductible';

const BICYCLES_COVERAGE = 'PRCBicycleCov';
const BICYCLES_DEDUCTIBLE = 'PRCBicycleCovDeductible';

const LINE_PREFIX_PRC = 'PRC';

const getPrivateContentAdjustDetailsConfig = (
    privateContent: PrivateContentDTO,
    changes: DiffDTO[],
    isOpc: boolean
): OPCRowInformation[] => {
    const isBaggageDeductibleAvailableForUpdate = isChangeTypeAvailable(
        {
            type: 'term',
            coverageId: CONTENT_BAGGAGE_COVERAGE,
            termId: CONTENT_BAGGAGE_DEDUCTIBLE
        },
        changes
    );

    return [
        {
            key: AdjustItemKeys.PRC_PRIVATE_CONTENT_CONTENT_BAGGAGE_DEDUCTIBLE,
            name:
                getCoverageFromCoverable(privateContent.coverages, CONTENT_BAGGAGE_COVERAGE)
                    ?.name ?? '',
            fieldValue: getTermMonetaryValue(
                privateContent,
                CONTENT_BAGGAGE_COVERAGE,
                CONTENT_BAGGAGE_DEDUCTIBLE
            ),
            isAdjustableItem: true,
            labelType: 'modalLink',
            opcInfo: {
                changeType: 'tilpas',
                productName: 'top_prc_content',
                coverableId: privateContent.fixedId ?? '',
                coverageId: CONTENT_BAGGAGE_COVERAGE,
                adjustAction: 'Deductible',
                termId: CONTENT_BAGGAGE_DEDUCTIBLE
            },
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: 'DSS-CON -HIGHLIGHTED-TEXT-MODAL-AMOUNT-OF-RESIDENTS-TITLE',
                    description: 'DSS-CON -HIGHLIGHTED-TEXT-MODAL-AMOUNT-OF-RESIDENTS'
                }
            },
            hidden: isOpc && !isBaggageDeductibleAvailableForUpdate
        }
    ];
};

const getYouthContentAdjustDetailsConfig = (
    youthContent: YouthContentDTO,
    changes: DiffDTO[],
    isOpc: boolean
): OPCRowInformation[] => {
    const isBaggageDeductibleAvailableForUpdate = isChangeTypeAvailable(
        {
            type: 'term',
            coverageId: CONTENT_BAGGAGE_COVERAGE,
            termId: CONTENT_BAGGAGE_DEDUCTIBLE
        },
        changes
    );
    const isBicycleDeductibleAvailableForUpdate = isChangeTypeAvailable(
        {
            type: 'term',
            coverageId: BICYCLES_COVERAGE,
            termId: BICYCLES_DEDUCTIBLE
        },
        changes
    );

    return [
        {
            key: AdjustItemKeys.PRC_YOUTH_CONTENT_CONTENT_BAGGAGE_DEDUCTIBLE,
            name:
                getCoverageFromCoverable(youthContent.coverages, CONTENT_BAGGAGE_COVERAGE)?.name ??
                '',
            fieldValue: getTermMonetaryValue(
                youthContent,
                CONTENT_BAGGAGE_COVERAGE,
                CONTENT_BAGGAGE_DEDUCTIBLE
            ),
            isAdjustableItem: true,
            labelType: 'modalLink',
            opcInfo: {
                changeType: 'tilpas',
                productName: 'top_prc_youth_content',
                coverableId: youthContent.fixedId ?? '',
                coverageId: CONTENT_BAGGAGE_COVERAGE,
                adjustAction: 'Deductible',
                termId: CONTENT_BAGGAGE_DEDUCTIBLE
            },
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: 'DSS-CON -HIGHLIGHTED-TEXT-MODAL-AMOUNT-OF-RESIDENTS-TITLE',
                    description: 'DSS-CON -HIGHLIGHTED-TEXT-MODAL-AMOUNT-OF-RESIDENTS-YOUTH'
                }
            },
            hidden: isOpc && !isBaggageDeductibleAvailableForUpdate
        },
        {
            key: AdjustItemKeys.PRC_YOUTH_CONTENT_BICYCLES_DEDUCTIBLE,
            name: getCoverageFromCoverable(youthContent.coverages, BICYCLES_COVERAGE)?.name ?? '',
            fieldValue: getTermMonetaryValue(youthContent, BICYCLES_COVERAGE, BICYCLES_DEDUCTIBLE),
            isAdjustableItem: true,
            isRemovableItem: true,
            labelType: 'modalLink',
            opcInfo: {
                changeType: 'tilpas',
                productName: 'top_prc_youth_content',
                coverableId: youthContent.fixedId ?? '',
                coverageId: BICYCLES_COVERAGE,
                adjustAction: 'Deductible',
                termId: BICYCLES_DEDUCTIBLE
            },
            modalInformation: {
                modalType: ViewHippoInformationModal,
                modalProps: {
                    title: 'DSS-CON-HIGHLIGHTED-TEXT-MODAL-BICYCLE-PARTS-TITLE',
                    description: 'DSS-CON-HIGHLIGHTED-TEXT-MODAL-BICYCLES-DESCRIPTION'
                }
            },
            hidden: isOpc && !isBicycleDeductibleAvailableForUpdate
        }
    ];
};

const getContentAdjustDetailsConfig = (
    content: CoverableDTO,
    isOpc: boolean,
    coverableChangesDTO: CoverableChangesDTO[]
): OPCRowInformation[] => {
    const changes = getCoverableDiffsDTO(
        coverableChangesDTO,
        LINE_PREFIX_PRC,
        content.fixedId ?? ''
    );

    switch (content.flavourName) {
        case 'Indboforsikring':
            return getPrivateContentAdjustDetailsConfig(content, changes, isOpc);
        case 'Indboforsikring til unge':
            return getYouthContentAdjustDetailsConfig(content, changes, isOpc);
        default:
            throw new Error('Coverable is not content flavour');
    }
};

export default getContentAdjustDetailsConfig;
